'use client';

import type { ReactNode } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

interface DashboardCardProps {
  children: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  /** Adds a subtle lift on hover — used for interactive/linkable cards. */
  interactive?: boolean;
  contentSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}

/**
 * Shared surface for every dashboard block. Wraps MUI <Card> with the design
 * language used across the redesign — flat border, 18px radius (from the theme),
 * consistent header row — so all sections read as one system in light and dark.
 */
export const DashboardCard = ({
  children,
  title,
  subtitle,
  action,
  icon,
  interactive = false,
  contentSx,
  sx,
}: DashboardCardProps) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        boxShadow: 'none',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        ...(interactive && {
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: 'primary.main',
            boxShadow: (theme) =>
              `0 10px 30px ${
                theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(15,23,42,0.10)'
              }`,
          },
        }),
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2.5, md: 3 },
            pt: { xs: 2.5, md: 3 },
            pb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  color: 'primary.main',
                  bgcolor: (theme) =>
                    alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.1),
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
                  noWrap
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {action}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2.5, md: 3 },
          pb: { xs: 2.5, md: 3 },
          pt: title ? 0 : { xs: 2.5, md: 3 },
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Card>
  );
};
